import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'

interface PointData {
  date: string
  points: number
}

interface PointsChartProps {
  data: PointData[]
}

interface CustomTooltipProps {
  active?: boolean
  payload?: Array<{ value: number }>
  label?: string
}

// Custom tooltip component
function CustomTooltip({ active, payload, label }: CustomTooltipProps) {
  if (!active || !payload || !payload.length || !label) {
    return null
  }

  const date = new Date(label)
  const formattedDate = date.toLocaleDateString('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  })

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3">
      <p className="text-sm text-gray-600 capitalize">{formattedDate}</p>
      <p className="text-lg font-bold text-tessan-primary">
        +{payload[0].value} pts
      </p>
    </div>
  )
}

// Format date for X axis
function formatXAxis(dateStr: string): string {
  const date = new Date(dateStr)
  return date.toLocaleDateString('fr-FR', { day: 'numeric' })
}

export function PointsChart({ data }: PointsChartProps) {
  if (data.length === 0) {
    return (
      <div className="h-48 flex items-center justify-center text-gray-500">
        Aucune donnée disponible pour les 30 derniers jours.
      </div>
    )
  }

  const totalPoints = data.reduce((sum, d) => sum + d.points, 0)
  const avgPoints = Math.round(totalPoints / data.length)
  const maxPoints = Math.max(...data.map(d => d.points))

  return (
    <div className="space-y-4">
      {/* Summary stats */}
      <div className="flex flex-wrap gap-4 text-sm">
        <div className="flex items-center gap-2">
          <span className="text-gray-500">Total:</span>
          <span className="font-semibold text-tessan-primary">+{totalPoints} pts</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-gray-500">Moyenne/jour:</span>
          <span className="font-semibold text-tessan-primary">+{avgPoints} pts</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-gray-500">Meilleur jour:</span>
          <span className="font-semibold text-tessan-primary">+{maxPoints} pts</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-gray-500">Jours actifs:</span>
          <span className="font-semibold text-gray-700">{data.length}</span>
        </div>
      </div>

      {/* Recharts bar chart */}
      <div className="h-56">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={data}
            margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
          >
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
            <XAxis
              dataKey="date"
              tickFormatter={formatXAxis}
              tick={{ fontSize: 11, fill: '#6b7280' }}
              tickLine={false}
              axisLine={{ stroke: '#e5e7eb' }}
              interval="preserveStartEnd"
              minTickGap={30}
            />
            <YAxis
              tick={{ fontSize: 11, fill: '#6b7280' }}
              tickLine={false}
              axisLine={false}
              tickFormatter={(value) => `${value}`}
            />
            <Tooltip
              content={<CustomTooltip />}
              cursor={{ fill: 'rgba(15, 53, 45, 0.1)' }}
            />
            <Bar
              dataKey="points"
              fill="#0F352D"
              radius={[4, 4, 0, 0]}
              maxBarSize={40}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Legend */}
      <div className="flex justify-center">
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <div className="w-3 h-3 bg-tessan-primary rounded" />
          <span>Points gagnés par jour</span>
        </div>
      </div>
    </div>
  )
}
